
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

import { Courier, Provider } from '@/new-services/types/couriers'
import { AssignCointainersToCourierPayload } from '@/new-services/types/planning'

import couriersAPI from '@/new-services/couriers.service'

import useNotifications from '@/common/composable/useNotifications'

import useVuelidate from '@vuelidate/core'
import { requiredIf } from '@vuelidate/validators'

const TAKE_TYPE = 2

export default defineComponent({
    components: {
        NewModal,
        NewSelect,
        NewCheckbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        containersIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        cityIds: {
            type: Array as PropType<number[]>,
            required: true,
            default: () => [],
        },
        dispatcherSectorIds: {
            type: Array as PropType<number[]>,
            required: true,
            default: () => [],
        },
    },
    emits: ['submit', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            //
            courier_id: null as number | null,
            provider_id: null as number | null,
        })

        const validation$ = useVuelidate(
            {
                courier_id: { required: requiredIf(() => !state.provider_id) },
                provider_id: { required: requiredIf(() => !state.courier_id) },
            },
            state
        )

        const couriers_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Courier[],
            providers: [] as Provider[],
        })

        const providers_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Provider[],
        })

        const loadCouriers = async () => {
            try {
                couriers_state.is_loading = true

                const response = await couriersAPI.getCouriers({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds: props.dispatcherSectorIds,
                    statusIds: [2],
                })
                couriers_state.list = response.data.data

                couriers_state.is_failed = false
            } catch (error) {
                console.error(error)
                couriers_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка курьеров произошла ошибка',
                })
            } finally {
                couriers_state.is_loading = false
            }
        }

        const loadProviders = async () => {
            try {
                providers_state.is_loading = true

                const params = {
                    type_id: TAKE_TYPE,
                    city_ids: props.cityIds,
                }

                const response = await couriersAPI.getProviders(params)
                providers_state.list = response.data.data

                providers_state.is_failed = false
            } catch (error) {
                console.error(error)
                providers_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка поставщиков произошла ошибка',
                })
            } finally {
                providers_state.is_loading = false
            }
        }

        const onSubmit = () => {
            if (props.loading) return

            validation$.value.$touch()
            if (validation$.value.$invalid) {
                return
            }

            const payload: AssignCointainersToCourierPayload = {
                courierId: state.courier_id as number,
                provider_id: state.provider_id as number,
                containerIds: props.containersIds,
            }

            emit('submit', payload)
        }

        const onCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const onProviderChange = (provider_id: number) => {
            if (state.provider_id === provider_id) {
                state.provider_id = null
                return
            }

            state.provider_id = provider_id
        }

        const isProviderChecked = (provider_id: number) => {
            return provider_id === state.provider_id
        }

        // computed

        const isLoading = computed(
            () => props.loading || couriers_state.is_loading || providers_state.is_loading
        )
        const isProviders = computed(() => {
            return Boolean(providers_state.list.length)
        })

        //

        onBeforeMount(() => {
            loadCouriers()
            loadProviders()
        })

        //

        return {
            state,
            validation$,
            couriers_state,
            providers_state,
            //
            loadCouriers,
            onSubmit,
            onCancel,
            onProviderChange,
            isProviderChecked,
            //
            isLoading,
            isProviders,
        }
    },
})
